/* ============================================================
   メタオブジェクトお気に入りボタン（ブランド / コーディネート / スタッフ）
   - ブランド一覧（カード形式 / タブ形式）の [data-brand-favorite]、
     コーディネート一覧・ピックアップの [data-coordination-favorite]、
     スタッフランキングの [data-staff-favorite] を一括制御
   - メタオブジェクトお気に入りを App Proxy API で登録/解除
     ブランド: metaobject_type = brand_setting
     コーディネート: metaobject_type = coord_setting、metaobject_id = FAANS のコーデID（add / remove とも）
       （coord_setting の coord_id。顧客メタフィールド multi_likes.coordination とレスポンスの
         wishlist[].id もコーデID なので、登録済み判定はコーデID 同士で比較する）
     スタッフ: metaobject_type = staff_setting（仮の名称）、metaobject_id = FAANS のスタッフID（members[].id）
       （コーディネートと同じ方式。multi_likes.staff と wishlist[].id もスタッフID）
   - POST は JSON 固定（Content-Type: application/json）
   - 楽観更新（先にUI反映）＋ 失敗時ロールバック、処理中はお気に入りボタンを非活性、古いレスポンス破棄
   - クリックは document への委譲で拾う（custom-coordinate.js / custom-staff.js が後から描画するカードも再バインド不要）
   - 初期状態:
       ブランド        : Liquid（customer.metafields.multi_likes.brand）で描画済み
       コーディネート  : window.multilikesProductConfig.favoriteCoordinationIds から hydrate() で反映
       スタッフ        : window.multilikesProductConfig.favoriteStaffIds から hydrate() で反映
   - 公開 API: window.MultilikesMetaobjectWishlist = { hydrate, applyWishlist, normalizeId }
   ============================================================ */
(function () {
  'use strict';

  var DEFAULT_ENDPOINT = '/apps/multilikes-endpoint';

  // 種別ごとの契約。activeClasses は全て同時に付け外しする
  // （コーディネートは .c-wishlist-button-icon.is-active が塗りスタイル、.is-favorited はブランドと揃えるため）
  var KINDS = {
    brand: {
      selector: '[data-brand-favorite]',
      defaultType: 'brand_setting',
      activeClasses: ['is-favorited'],
      event: 'multilikes:brand-wishlist:changed',
      configIdsKey: 'favoriteBrandIds',
      labelAdd: 'お気に入りに追加',
      labelRemove: 'お気に入りから削除'
    },
    coordination: {
      selector: '[data-coordination-favorite]',
      defaultType: 'coord_setting',
      activeClasses: ['is-active', 'is-favorited'],
      event: 'multilikes:coordination-wishlist:changed',
      configIdsKey: 'favoriteCoordinationIds',
      labelAdd: 'お気に入りに追加',
      labelRemove: 'お気に入りから削除'
    },
    staff: {
      selector: '[data-staff-favorite]',
      defaultType: 'staff_setting',
      activeClasses: ['is-active', 'is-favorited'],
      event: 'multilikes:staff-wishlist:changed',
      configIdsKey: 'favoriteStaffIds',
      // 状態は aria-pressed で伝えるため、ラベルは状態によらず固定
      labelAdd: 'お気に入り',
      labelRemove: 'お気に入り'
    }
  };
  // config の ID 一覧から初期状態を反映する種別（ブランドは Liquid 描画済みのため含めない）
  var HYDRATE_KINDS = ['coordination', 'staff'];
  var KIND_NAMES = Object.keys(KINDS);
  var ALL_SELECTOR = KIND_NAMES.map(function (name) { return KINDS[name].selector; }).join(',');

  function getConfig() {
    return window.multilikesProductConfig || {};
  }

  function getCustomerId() {
    return getConfig().customerId || null;
  }

  function getEndpoint() {
    return getConfig().defaultUrl || DEFAULT_ENDPOINT;
  }

  function getErrorText() {
    var cfg = getConfig();
    return (cfg.texts && cfg.texts.error) || 'お気に入りの更新に失敗しました';
  }

  function redirectToLogin() {
    // ログイン後に元のページへ戻す。Shopify は同一オリジンの相対パスのみ return_url として許可するため、
    // フルURL(window.location.href)ではなく pathname + search を渡す。loginUrl はロケール接頭辞対応で config 由来。
    var loginUrl = getConfig().loginUrl || '/account/login';
    var returnPath = window.location.pathname + window.location.search;
    window.location.href = loginUrl + '?return_url=' + encodeURIComponent(returnPath);
  }

  function acquireWishlistButtonLock(fallbackButton) {
    var lock = window.multilikesWishlistButtonLock;
    if (lock && typeof lock.acquire === 'function') {
      return lock.acquire();
    }

    if (fallbackButton) fallbackButton.disabled = true;
    return function () {
      if (fallbackButton) fallbackButton.disabled = false;
    };
  }

  function kindOf(btn) {
    for (var i = 0; i < KIND_NAMES.length; i++) {
      if (btn.matches(KINDS[KIND_NAMES[i]].selector)) return KINDS[KIND_NAMES[i]];
    }
    return null;
  }

  // 各ボタンのリクエスト連番（古いレスポンスを破棄するため）
  var btnSeq = new WeakMap();
  function nextSeq(btn) {
    var n = (btnSeq.get(btn) || 0) + 1;
    btnSeq.set(btn, n);
    return n;
  }
  function isLatest(btn, seq) {
    return btnSeq.get(btn) === seq;
  }

  function normalizeId(value) {
    if (value == null) return '';
    var text = String(value).trim();
    var match = text.match(/\/(\d+)$/);
    return match ? match[1] : text;
  }

  function isButtonActive(btn, kind) {
    return btn.classList.contains(kind.activeClasses[0]);
  }

  function setButtonState(btn, kind, isFavorited) {
    kind.activeClasses.forEach(function (cls) {
      btn.classList.toggle(cls, isFavorited);
    });
    btn.setAttribute('aria-pressed', isFavorited ? 'true' : 'false');
    // aria-label を持つボタンだけ文言を追従させる（ブランドはテキストラベル併記のため付けていない）
    if (btn.hasAttribute('aria-label')) {
      btn.setAttribute('aria-label', isFavorited ? kind.labelRemove : kind.labelAdd);
    }
  }

  function idsToSet(list) {
    var set = new Set();
    if (!Array.isArray(list)) return set;
    list.forEach(function (item) {
      var raw = item && typeof item === 'object' ? item.id : item;
      var id = normalizeId(raw);
      if (id) set.add(id);
    });
    return set;
  }

  function getConfigIds(kind) {
    return idsToSet(getConfig()[kind.configIdsKey]);
  }

  function setConfigIds(kind, idSet) {
    var cfg = window.multilikesProductConfig;
    if (!cfg) return;
    cfg[kind.configIdsKey] = Array.from(idSet);
  }

  // レスポンスの wishlist（顧客の全お気に入りID）を正として、
  // 同種・同一 metaobject-id を持つ全ボタンを再同期し、config の ID 一覧も書き戻す
  // （後から描画されるカードの初期状態に使うため）
  function applyWishlist(kindName, wishlist) {
    var kind = KINDS[kindName];
    if (!kind || !Array.isArray(wishlist)) return;
    var ids = idsToSet(wishlist);
    document.querySelectorAll(kind.selector).forEach(function (btn) {
      var id = btn.getAttribute('data-metaobject-id');
      if (!id) return;
      setButtonState(btn, kind, ids.has(normalizeId(id)));
    });
    setConfigIds(kind, ids);
  }

  // config の ID 一覧から root 配下のボタンの初期状態を反映する。
  // custom-coordinate.js が描画後に呼ぶ。起動時にも 1 回実行（Liquid 描画済みのピックアップ等）
  function hydrate(root, kindName) {
    var scope = root || document;
    KIND_NAMES.forEach(function (name) {
      if (kindName && name !== kindName) return;
      var kind = KINDS[name];
      var cfg = getConfig();
      // config に一覧が無い種別は Liquid 描画済みの状態を尊重して触らない
      if (!Array.isArray(cfg[kind.configIdsKey])) return;
      var ids = idsToSet(cfg[kind.configIdsKey]);
      var buttons = [];
      if (scope.matches && scope.matches(kind.selector)) buttons.push(scope);
      if (scope.querySelectorAll) {
        buttons = buttons.concat(Array.prototype.slice.call(scope.querySelectorAll(kind.selector)));
      }
      buttons.forEach(function (btn) {
        var id = btn.getAttribute('data-metaobject-id');
        if (!id) return;
        setButtonState(btn, kind, ids.has(normalizeId(id)));
      });
    });
  }

  function wishlistContainsId(wishlist, id) {
    if (!Array.isArray(wishlist)) return false;
    var target = normalizeId(id);
    return wishlist.some(function (item) {
      var raw = item && typeof item === 'object' ? item.id : item;
      return raw != null && normalizeId(raw) === target;
    });
  }

  function dispatchChanged(kind, detail) {
    document.dispatchEvent(new CustomEvent(kind.event, { detail: detail }));
  }

  function toggleFavorite(btn, kind) {
    var customerId = getCustomerId();
    if (!customerId) {
      redirectToLogin();
      return;
    }

    var metaobjectId = btn.getAttribute('data-metaobject-id');
    if (!metaobjectId) return;
    var metaobjectType = btn.getAttribute('data-metaobject-type') || kind.defaultType;

    var previous = isButtonActive(btn, kind);
    var next = !previous;
    var action = next ? 'add' : 'remove';

    var releaseButtonLock = acquireWishlistButtonLock(btn);

    // 楽観更新：先にUIを反映
    setButtonState(btn, kind, next);
    var seq = nextSeq(btn);

    fetch(getEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        target_type: 'metaobject',
        customer_id: customerId,
        action: action,
        metaobject_type: metaobjectType,
        metaobject_id: metaobjectId
      })
    })
      .then(function (res) {
        return res.json().then(function (json) { return { ok: res.ok, json: json }; });
      })
      .then(function (r) {
        if (!isLatest(btn, seq)) return;
        if (!r.ok) throw new Error((r.json && r.json.error) || 'request failed');
        var data = (r.json && r.json.data) || {};
        if (r.json && r.json.error) throw new Error(r.json.error);
        var resolved = next;
        if (Array.isArray(data.wishlist)) {
          applyWishlist(kindNameOf(kind), data.wishlist);
          resolved = wishlistContainsId(data.wishlist, metaobjectId);
        } else if (typeof data.isInWishlist === 'boolean') {
          resolved = data.isInWishlist;
          setButtonState(btn, kind, resolved);
          syncConfigId(kind, metaobjectId, resolved);
        } else {
          syncConfigId(kind, metaobjectId, resolved);
        }
        dispatchChanged(kind, {
          action: action,
          metaobjectId: metaobjectId,
          metaobjectType: metaobjectType,
          isInWishlist: resolved,
          wishlist: data.wishlist
        });
      })
      .catch(function (e) {
        if (!isLatest(btn, seq)) return;
        // ロールバック
        setButtonState(btn, kind, previous);
        window.alert(getErrorText());
        console.error('Metaobject wishlist update failed (' + metaobjectType + '):', e);
      })
      .then(function () {
        releaseButtonLock();
      });
  }

  function kindNameOf(kind) {
    for (var i = 0; i < KIND_NAMES.length; i++) {
      if (KINDS[KIND_NAMES[i]] === kind) return KIND_NAMES[i];
    }
    return null;
  }

  // wishlist 配列が返らないレスポンスでも config の ID 一覧を追従させる
  function syncConfigId(kind, metaobjectId, isFavorited) {
    var cfg = window.multilikesProductConfig;
    if (!cfg || !Array.isArray(cfg[kind.configIdsKey])) return;
    var ids = idsToSet(cfg[kind.configIdsKey]);
    var id = normalizeId(metaobjectId);
    if (isFavorited) ids.add(id); else ids.delete(id);
    setConfigIds(kind, ids);
  }

  // document 委譲：後から innerHTML で追加されたボタンも拾う
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var btn = target.closest(ALL_SELECTOR);
    if (!btn) return;
    var kind = kindOf(btn);
    if (!kind) return;
    event.preventDefault();
    event.stopPropagation();
    if (btn.disabled) return;
    toggleFavorite(btn, kind);
  });

  window.MultilikesMetaobjectWishlist = {
    hydrate: hydrate,
    applyWishlist: applyWishlist,
    normalizeId: normalizeId
  };

  // コーディネート・スタッフのみ config 由来の初期状態を反映（ブランドは Liquid 描画済み）
  function hydrateConfigKinds(root) {
    HYDRATE_KINDS.forEach(function (name) { hydrate(root, name); });
  }

  function init() {
    hydrateConfigKinds(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // テーマエディタでセクション再描画された場合に初期状態を再反映
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) hydrateConfigKinds(event.target);
  });
})();
